// v2.7.1 — Card pour un profil Justice (avocat / huissier / notaire / etc.)
// 3 actions : 🧭 Y aller (vers /navigate), 📞 Appeler (tel:), 📋 Copier (presse-papier).

import { toast } from "sonner";
import SponsoredBadge, { VerifiedTick } from "./SponsoredBadge";

function _hashColor(str) {
  // Couleur déterministe (palette dark) à partir du nom du profil.
  const palette = [
    "#1E40AF", "#3B82F6", "#0EA5E9", "#16A34A", "#15803D",
    "#A855F7", "#7C3AED", "#DB2777", "#DC2626", "#EA580C",
    "#CA8A04", "#0891B2",
  ];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function _initials(name) {
  if (!name) return "?";
  const cleaned = name.replace(/^Maître\s+/i, "").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

export default function JusticeCard({ profile, onNavigate }) {
  if (!profile) return null;
  const avatarColor = _hashColor(profile.nom || profile.id);
  const initials = _initials(profile.nom);

  const goNavigate = () => {
    const url = `/navigate?dest=${profile.lat},${profile.lng}&code=${encodeURIComponent(profile.adresse_civ)}&label=${encodeURIComponent(profile.specialite || profile.nom)}`;
    if (onNavigate) onNavigate(url, profile);
    else window.location.href = url;
  };

  const callPhone = () => {
    window.location.href = `tel:${(profile.telephone || "").replace(/\s+/g, "")}`;
  };

  const copyProfile = async () => {
    const lines = [
      profile.nom,
      profile.specialite ? `Spécialité : ${profile.specialite}` : null,
      profile.annee_barreau ? `Barreau ${profile.annee_barreau} · ${profile.annees_experience} ans d'expérience` :
        (profile.annees_experience ? `${profile.annees_experience} ans d'expérience` : null),
      `📍 ${profile.adresse_civ} — ${profile.adresse_textuelle}`,
      `📞 ${profile.telephone}`,
      profile.langues_parlees ? `💬 ${profile.langues_parlees.join(", ")}` : null,
      profile.honoraires_estimes ? `💰 ${profile.honoraires_estimes}` : null,
    ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success("Profil copié");
    } catch (_) {
      toast.error("Impossible de copier");
    }
  };

  return (
    <div data-testid={`justice-card-${profile.id}`} className="civ-mode-card" style={{
      background: "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
      border: "1px solid rgba(255,255,255,0.12)", borderRadius: 14, padding: 16,
      color: "#fff", fontFamily: "system-ui, -apple-system, sans-serif",
      minWidth: 280, maxWidth: 320, width: 280,
      flex: "0 0 auto", scrollSnapAlign: "start",
    }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: avatarColor, display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18, fontWeight: 700, flexShrink: 0,
        }} data-testid="justice-avatar">{initials}</div>
        <div style={{ minWidth: 0 }}>
          {(profile.sponsored_tier && profile.sponsored_tier !== "free") && (
            <div style={{ marginBottom: 4 }} data-testid={`justice-badges-${profile.id}`}>
              <SponsoredBadge tier={profile.sponsored_tier} size="sm" />
            </div>
          )}
          <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.25, display: "inline-flex", alignItems: "center" }}>
            {profile.nom}
            {profile.verified && <VerifiedTick size="sm" />}
          </div>
          <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 2 }}>⚖️ {profile.specialite}</div>
        </div>
      </div>

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10, marginBottom: 10, fontSize: 12, lineHeight: 1.55 }}>
        <div style={{ color: "#cbd5e1" }}>
          📍 <code style={{ fontSize: 11, color: "#fff" }}>{profile.adresse_civ}</code>
        </div>
        <div style={{ color: "#94a3b8", fontSize: 11, marginLeft: 18 }}>{profile.adresse_textuelle}</div>
        <div style={{ color: "#cbd5e1", marginTop: 4 }}>📞 <span style={{ color: "#fff" }}>{profile.telephone}</span></div>
        {profile.annees_experience != null && (
          <div style={{ color: "#cbd5e1", marginTop: 4 }}>
            📅 {profile.annees_experience} ans d'expérience{profile.annee_barreau ? ` · Barreau ${profile.annee_barreau}` : ""}
          </div>
        )}
        {profile.langues_parlees && profile.langues_parlees.length > 0 && (
          <div style={{ color: "#cbd5e1", marginTop: 4 }}>💬 {profile.langues_parlees.join(", ")}</div>
        )}
        {profile.honoraires_estimes && (
          <div style={{ color: "#cbd5e1", marginTop: 4 }}>💰 {profile.honoraires_estimes}</div>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }}>
        <button onClick={goNavigate} data-testid="justice-btn-navigate" style={{
          flex: 1, background: "#3B82F6", color: "#fff", border: "none",
          padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12,
        }}>🧭 Y aller</button>
        <button onClick={callPhone} data-testid="justice-btn-call" style={{
          flex: 1, background: "#16A34A", color: "#fff", border: "none",
          padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12,
        }}>📞 Appeler</button>
        <button onClick={copyProfile} data-testid="justice-btn-copy" style={{
          flex: 1, background: "rgba(255,255,255,0.08)", color: "#fff",
          border: "1px solid rgba(255,255,255,0.15)",
          padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12,
        }}>📋 Copier</button>
      </div>
    </div>
  );
}
